import {
  AddTablePatternPayload,
  UpdateTablePatternPayload,
  VerifcaitonNewPatternPayloadType,
  VerificationBatchListTypes,
  VerificationFieldListTypes,
  VerificationFieldListTypesResponse,
  VerificationFileDetails,
  VerificationPatternsListTypes
} from 'types/verification';
import baseApi, { API_V1 } from './base';

const BASE_API = `${API_V1}/Verification`;

const verificationApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getBatchList: build.query<any[], { docTypeID: number; screenType: string }>({
      query: ({ docTypeID, screenType }) => ({
        url: `${API_V1}/Assurance/GetBatchDetails`,
        params: { DocTypeId: docTypeID, screenType: screenType }
      }),
      transformResponse: (responseData: { data: any[] }) =>
        responseData.data
          ? responseData.data.map((row) => ({ ...row, batchStatus: String(row.batchStatus).replaceAll(' ', '').toUpperCase() }))
          : []
    }),

    getVerificationDetails: build.query<any[], { batchID: number; DocTypeID: any }>({
      query: ({ batchID, DocTypeID = 0 }) => ({
        url: `${BASE_API}/GetVerificationListDetails`,
        params: { BatchID: batchID, DocTypeID: DocTypeID }
      }),
      transformResponse: (responseData: { data: any[] }) =>
        responseData.data
          ? responseData.data.map((row) => ({
              ...row,
              batchStatus: String(row.batchStatus ?? 'ready')
                .replaceAll(' ', '')
                .toUpperCase(),
              status: String(row.status ?? 'ready')
                .replaceAll(' ', '')
                .toUpperCase()
            }))
          : []
    }),

    getVerificationBatchDetails: build.query<VerificationBatchListTypes[], { docTypeID: number }>({
      query: ({ docTypeID }) => ({
        url: `${BASE_API}/GetVerificationBatchDetails`,
        params: { DocTypeID: docTypeID }
      }),
      transformResponse: (responseData: { data: VerificationBatchListTypes[] }, _meta, arg) =>
        (responseData?.data).map((batchDetail) => ({
          ...batchDetail,
          status: String(batchDetail?.status).replaceAll(' ', '').toUpperCase(),
          parentDoctypeID: String(arg.docTypeID)
        }))
    }),
    getVerificationDocTypeFields: build.query<VerificationFieldListTypes[], { docTypeID: number; batchID: number }>({
      query: ({ docTypeID, batchID }) => ({
        url: `${BASE_API}/GetVerificationFieldListDetails`,
        params: { DocTypeID: docTypeID, BatchID: batchID }
      }),
      transformResponse: (responseData: { data: VerificationFieldListTypesResponse[] }, _meta, arg) => {
        let returnData: VerificationFieldListTypes[] = [];

        if (responseData?.data) {
          const groupedData = Object.groupBy(responseData.data, ({ fieldID }) => fieldID);
          for (let fieldId in groupedData) {
            if (groupedData?.[fieldId]?.[0]) {
              returnData.push({
                doctypeID: groupedData[fieldId][0].doctypeID,
                fieldID: groupedData[fieldId][0].fieldID,
                fieldName: groupedData[fieldId][0].fieldName,
                techniques: groupedData[fieldId] || []
              });
            }
          }
        }
        return returnData;
      }
    }),
    getVerificationFileDetails: build.query<VerificationFileDetails[], { docTypeID: number; batchId: number }>({
      query: ({ docTypeID, batchId }) => ({
        url: `${BASE_API}/GetVerificationFileDetails`,
        params: { DocTypeID: docTypeID, BatchID: batchId }
      }),
      transformResponse: (responseData: { data: VerificationFileDetails[] }) => responseData.data || []
    }),
    getVerificationPatternList: build.query<VerificationPatternsListTypes[], { docTypeID: number; batchID?: number }>({
      query: ({ docTypeID, batchID }) => ({
        url: `${API_V1}/Pattern/GetPattern`,
        params: { DocTypeID: docTypeID, BatchID: batchID }
      }),
      transformResponse: (responseData: { data: VerificationPatternsListTypes[] }) => responseData.data || []
    }),
    verificationStatusUpdate: build.mutation<any, { batchID: number; isFinish?: boolean; isBack?: boolean }>({
      query: ({ batchID, isBack: IsBack = false, isFinish: IsFinish = false }) => ({
        url: `${API_V1}/Pattern/VerificationStatusUpdate`,
        params: { BatchID: batchID, IsBack, IsFinish },
        method: 'PUT'
      })
    }),
    verificationAddNewPattern: build.mutation<void, VerifcaitonNewPatternPayloadType>({
      query: (data) => ({
        url: `${API_V1}/Pattern/AddHeaderPattern`,
        data,
        method: 'POST',
        cheaders: 'application/*+json'
      })
    }),
    verificationDeletePattern: build.mutation<void, { taxonomyID: number }>({
      query: ({ taxonomyID }) => ({
        url: `${API_V1}/Pattern/DeleteHeaderData`,
        params: { TaxonomyID: taxonomyID },
        method: 'DELETE'
      })
    }),
    verificationUpdatePattern: build.mutation<void, VerifcaitonNewPatternPayloadType>({
      query: (data) => ({
        url: `${API_V1}/Pattern/UpdateHeaderData`,
        data,
        method: 'PUT',
        cheaders: 'application/*+json'
      })
    }),
    addTablePattern: build.mutation<void, AddTablePatternPayload>({
      query: (data) => ({
        url: `${API_V1}/Pattern/AddTablePattern`,
        data,
        method: 'POST',
        cheaders: 'application/*+json'
      })
    }),
    updateTablePattern: build.mutation<void, UpdateTablePatternPayload>({
      query: (data) => ({
        url: `${API_V1}/Pattern/UpdateTablePattern`,
        data,
        method: 'PUT',
        cheaders: 'application/*+json'
      })
    }),
    getVerificationProcesses: build.query<any[], void>({
      query: () => ({
        url: `${API_V1}/Verification/GetVerificationProcessDetails`,
        params: {}
      })
    }),
    getVerifictaionDocumentTypes: build.query<any[], { processId: number }>({
      query: ({ processId }) => ({
        url: `${API_V1}/Verification/GetVerificationDocumentType`,
        params: { processId }
      }),
      transformResponse: (responseData: { data: any[] }) => responseData.data || []
    })
  })
});

export const {
  useGetBatchListQuery,
  useGetVerificationDetailsQuery,
  useLazyGetVerificationDetailsQuery,
  useLazyGetVerificationBatchDetailsQuery,
  useGetVerificationDocTypeFieldsQuery,
  useGetVerificationFileDetailsQuery,
  useGetVerificationBatchDetailsQuery,
  useGetVerificationPatternListQuery,
  useVerificationStatusUpdateMutation,
  useVerificationAddNewPatternMutation,
  useVerificationDeletePatternMutation,
  useVerificationUpdatePatternMutation,
  useAddTablePatternMutation,
  useUpdateTablePatternMutation,
  useGetVerificationProcessesQuery,
  useGetVerifictaionDocumentTypesQuery
} = verificationApi;
export default verificationApi;
